// ROUTE PATTERNS: a page's route may carry parameters.
//
// `/blog/:slug` is a page that answers `/blog/hello/` with `params.slug`
// set to `hello`; `/blog/:page?` answers `/blog/` and `/blog/2/` both. This
// module is the whole grammar and the only thing that reads it — the renderer
// matches a request against a partition's routes with it (api/code/src/render/
// resolve), the composer builds a concrete address out of a pattern with it
// (canonical, hreflang, a post's own URL), and the app tells a parameterised
// route from a literal one with it. Three readers of one small grammar is
// exactly why it is not written into any of them.
//
// A parameter is `:name`; a trailing `:name?` may be absent. That is all —
// no regex constraints, no wildcards, no optional segments in the middle.
// Gen-2 used path-to-regexp on a site's mountpoints and nothing on dev ever
// used more than a named segment; the constraint keeps a pattern's
// "specificity" a thing you can count (see literalCount).
//
// Exact routes are matched by string equality elsewhere and never come through
// `matchPattern`; a route with no `:` in it is not a pattern.

const NAME = /^[A-Za-z0-9-]+$/;

const segmentsOf = (route) =>
  String(route || "")
    .replace(/\/+$/, "")
    .split("/")
    .filter(Boolean);

export const isPattern = (route) => /(^|\/):/.test(String(route || ""));

// The pattern as parts, or null when it is not one this grammar admits — a
// `?` anywhere but on the last segment, an empty name, a stray character.
// A malformed pattern matches nothing rather than something surprising.
export const parsePattern = (pattern) => {
  const segments = segmentsOf(pattern);
  const parts = [];

  for (let i = 0; i < segments.length; i += 1) {
    const segment = segments[i];

    if (segment.startsWith(":")) {
      const optional = segment.endsWith("?");
      const name = segment.slice(1, optional ? -1 : undefined);

      if (!NAME.test(name) || (optional && i !== segments.length - 1)) {
        return null;
      }

      parts.push({ param: name, optional });
    } else {
      if (segment.includes("?")) {
        return null;
      }

      parts.push({ literal: segment });
    }
  }

  return parts;
};

// The params a path supplies for a pattern, or null when it does not match.
// Literal segments must agree exactly — the same rule literal routes follow,
// so `/Blog/x` and `/blog/x` stay different addresses.
export const matchPattern = (pattern, path) => {
  const parts = parsePattern(pattern);

  if (!parts) {
    return null;
  }

  const given = segmentsOf(path);
  const params = {};
  let at = 0;

  for (const part of parts) {
    if (part.literal !== undefined) {
      if (given[at] !== part.literal) {
        return null;
      }
      at += 1;
    } else if (given[at] === undefined) {
      if (!part.optional) {
        return null;
      }
    } else {
      params[part.param] = given[at];
      at += 1;
    }
  }

  return at === given.length ? params : null;
};

// How specific a pattern is: the number of segments it spells out. Between two
// patterns that both match, the one that says more wins — `/blog/:slug` over
// `/:section/:slug`.
export const literalCount = (pattern) => {
  const parts = parsePattern(pattern) || [];
  return parts.filter((part) => part.literal !== undefined).length;
};

// The concrete route for a pattern and a set of params: `/blog/:slug` with
// `{slug: "hello"}` is `/blog/hello`. A trailing optional param that is absent
// or empty is dropped; a required one that is absent makes the address
// unbuildable and the answer is null rather than a route with a colon in it.
// A literal route comes back as itself.
export const substitute = (pattern, params = {}) => {
  const parts = parsePattern(pattern);

  if (!parts) {
    return null;
  }

  const out = [];

  for (const part of parts) {
    if (part.literal !== undefined) {
      out.push(part.literal);
    } else {
      const value = params && params[part.param];

      if (value === undefined || value === null || value === "") {
        if (part.optional) {
          continue;
        }

        return null;
      }

      out.push(String(value));
    }
  }

  return `/${out.join("/")}`;
};
